/** A dust-sized star: a plain dot. */
export interface SpaceBackdropStar {
  opacity: number;
  radius: number;
  x: number;
  y: number;
}

/** A bright star: a four-pointed glint over a soft halo. */
export interface SpaceBackdropGlint {
  opacity: number;
  /** Tip-to-centre length of the glint's arms, in dp. */
  size: number;
  x: number;
  y: number;
}

export interface SpaceBackdropConstellation {
  /** Index pairs into `stars`. */
  edges: readonly (readonly [number, number])[];
  stars: readonly { readonly x: number; readonly y: number }[];
}

/** The band of the galaxy seen edge-on, as a rotated ellipse. */
export interface SpaceBackdropBand {
  angleDegrees: number;
  centerX: number;
  centerY: number;
  radiusX: number;
  radiusY: number;
}

export interface SpaceBackdropStarfield {
  band: SpaceBackdropBand;
  constellation: SpaceBackdropConstellation;
  glints: SpaceBackdropGlint[];
  stars: SpaceBackdropStar[];
}

/** One star per this many square dp, so a tablet is as dense as a phone. */
const DP2_PER_STAR = 850;
const MIN_STARS = 40;
const MAX_STARS = 900;
/** One glint per this many square dp. */
const DP2_PER_GLINT = 40_000;
const MIN_GLINTS = 4;
const MAX_GLINTS = 24;
/** The share of stars that crowd into the galaxy band rather than scattering evenly. */
const BAND_STAR_SHARE = 0.4;
const BAND_ANGLE_DEGREES = -32;

/**
 * 北斗 (the Big Dipper), handle first, in a unit box. Chinese astronomy's best-known asterism,
 * drawn faintly as a nod to the app's subject.
 */
const BEIDOU_STARS = [
  { x: 0, y: 0.18 },
  { x: 0.2, y: 0.05 },
  { x: 0.37, y: 0.1 },
  { x: 0.53, y: 0.22 },
  { x: 0.55, y: 0.48 },
  { x: 0.92, y: 0.52 },
  { x: 0.97, y: 0.2 },
] as const;
const BEIDOU_EDGES = [
  [0, 1],
  [1, 2],
  [2, 3],
  [3, 4],
  [4, 5],
  [5, 6],
  [6, 3],
] as const;

/** mulberry32: a tiny, fast, seedable PRNG, so a given size always draws the same sky. */
function createRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d_2b_79_f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4_294_967_296;
  };
}

/** An approximately normal sample (Irwin–Hall with three uniforms), mean 0 and spread about ±1. */
function gaussianish(random: () => number): number {
  return random() + random() + random() - 1.5;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Lays out a still night sky for a `width` × `height` dp backdrop: dust-sized stars (partly
 * crowded into a diagonal galaxy band), a handful of bright glints, and the Big Dipper.
 * Deterministic for a given size and seed.
 */
export function createSpaceBackdropStarfield({
  height,
  seed = 0x48_41_4e_21,
  width,
}: {
  height: number;
  seed?: number;
  width: number;
}): SpaceBackdropStarfield {
  const random = createRandom(seed);
  const area = Math.max(0, width) * Math.max(0, height);
  const diagonal = Math.hypot(width, height);

  const band: SpaceBackdropBand = {
    angleDegrees: BAND_ANGLE_DEGREES,
    centerX: width * 0.5,
    centerY: height * 0.52,
    radiusX: diagonal * 0.62,
    radiusY: Math.max(48, Math.min(width, height) * 0.2),
  };
  const bandAngle = (BAND_ANGLE_DEGREES * Math.PI) / 180;
  const bandCos = Math.cos(bandAngle);
  const bandSin = Math.sin(bandAngle);

  const starCount = area === 0 ? 0 : clamp(Math.round(area / DP2_PER_STAR), MIN_STARS, MAX_STARS);
  const stars: SpaceBackdropStar[] = [];
  for (let index = 0; index < starCount; index++) {
    let x: number;
    let y: number;
    if (random() < BAND_STAR_SHARE) {
      const along = (random() - 0.5) * 2 * band.radiusX;
      const across = gaussianish(random) * band.radiusY * 0.8;
      x = band.centerX + along * bandCos - across * bandSin;
      y = band.centerY + along * bandSin + across * bandCos;
      if (x < 0 || x > width || y < 0 || y > height) {
        x = random() * width;
        y = random() * height;
      }
    } else {
      x = random() * width;
      y = random() * height;
    }
    // Mostly specks, with the occasional larger star.
    const magnitude = random() ** 3;
    stars.push({
      opacity: 0.3 + 0.7 * random(),
      radius: 0.4 + 1.1 * magnitude,
      x,
      y,
    });
  }

  const glintCount =
    area === 0 ? 0 : clamp(Math.round(area / DP2_PER_GLINT), MIN_GLINTS, MAX_GLINTS);
  const glints: SpaceBackdropGlint[] = [];
  for (let index = 0; index < glintCount; index++) {
    glints.push({
      opacity: 0.55 + 0.45 * random(),
      // Small enough to read as a star, not a control, when one lands beside a toolbar button.
      size: 2.5 + 3 * random() ** 2,
      x: width * (0.04 + 0.92 * random()),
      y: height * (0.04 + 0.92 * random()),
    });
  }

  // Upper left, below the status bar and toolbar, clear of the band's centre; a fixed share of
  // the narrower side so it reads on a phone without sprawling on a tablet.
  const dipperSize = Math.min(width, height) * 0.42;
  const dipperLeft = width * 0.08;
  const dipperTop = height * 0.2;
  const constellation: SpaceBackdropConstellation = {
    edges: BEIDOU_EDGES,
    stars: BEIDOU_STARS.map((star) => ({
      x: dipperLeft + star.x * dipperSize,
      y: dipperTop + star.y * dipperSize,
    })),
  };

  return { band, constellation, glints, stars };
}
